import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { MainLayout } from '../layouts/MainLayout';
import { AuthLayout } from '../layouts/AuthLayout';
import { PublicLayout } from '../layouts/PublicLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { PermissionRoute } from './PermissionRoute';
import { NotFoundPage, ForbiddenPage } from '../pages';
import { Spinner } from '../components/ui/Spinner';
import { PERMISSIONS } from '@features/auth/utils/permissionRules';

// Lazy-loaded feature pages for code splitting
const LoginForm = lazy(() => import('../../features/auth/components/LoginForm').then(m => ({ default: m.LoginForm })));
const SSOCallback = lazy(() => import('../../features/auth/components/SSOCallback').then(m => ({ default: m.SSOCallback })));
const ForgotPasswordPage = lazy(() => import('../../features/auth/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../../features/auth/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ChatPage = lazy(() => import('../../features/chat').then(m => ({ default: m.ChatPage })));
const KnowledgeBasesListPage = lazy(() => import('../../features/knowledge-bases').then(m => ({ default: m.KnowledgeBasesListPage })));
const KnowledgeBaseDetailPage = lazy(() => import('../../features/knowledge-bases').then(m => ({ default: m.KnowledgeBaseDetailPage })));
const UsersPage = lazy(() => import('../../features/users').then(m => ({ default: m.UsersPage })));
const UserDetailPage = lazy(() => import('../../features/users').then(m => ({ default: m.UserDetailPage })));
const EmergencySubstitutionPage = lazy(() => import('../../features/emergency-substitution').then(m => ({ default: m.EmergencySubstitutionPage })));
const CreatePage = lazy(() => import('../../features/emergency-substitution').then(m => ({ default: m.CreatePage })));
const HistoryPage = lazy(() => import('../../features/emergency-substitution').then(m => ({ default: m.HistoryPage })));
const ReplyPage = lazy(() => import('../../features/emergency-substitution').then(m => ({ default: m.ReplyPage })));
const LocationAvailabilityReplyPage = lazy(() => import('../../features/public-responses').then(m => ({ default: m.LocationAvailabilityReplyPage })));
const WorkflowsListPage = lazy(() => import('../../features/onboarding').then(m => ({ default: m.WorkflowsListPage })));
const WorkflowBuilderPage = lazy(() => import('../../features/onboarding').then(m => ({ default: m.WorkflowBuilderPage })));
const InstancesListPage = lazy(() => import('../../features/onboarding').then(m => ({ default: m.InstancesListPage })));
const InstanceDetailPage = lazy(() => import('../../features/onboarding').then(m => ({ default: m.InstanceDetailPage })));
const EngagementPulsePage = lazy(() => import('../../features/engagement-pulse').then(m => ({ default: m.EngagementPulsePage })));
const EngagementPulseConfigPage = lazy(() => import('../../features/engagement-pulse/pages/EngagementPulseConfigPage').then(m => ({ default: m.EngagementPulseConfigPage })));
const ConversationsPage = lazy(() => import('../../features/admin-conversations').then(m => ({ default: m.ConversationsPage })));
const AuditLogsPage = lazy(() => import('../../features/files/pages/AuditLogsPage').then(m => ({ default: m.AuditLogsPage })));
const PublicStepPage = lazy(() => import('../../features/onboarding-public').then(m => ({ default: m.PublicStepPage })));

/**
 * Loading fallback component for lazy-loaded routes
 */
function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner size="lg" />
    </div>
  );
}

// Legacy components (to be migrated)
// AdminDashboard removed - functionality moved to /users route

/**
 * Application router configuration using React Router v6
 *
 * Route structure:
 * - Public routes: No authentication required
 * - Protected routes: Require authentication, wrapped with ProtectedRoute
 *
 * Layouts:
 * - MainLayout: Header with user info and logout (authenticated routes)
 * - AuthLayout: Simple wrapper for login page
 * - PublicLayout: Simple wrapper for public pages
 *
 * Code Splitting:
 * - All feature pages are lazy-loaded for optimal performance
 * - Each feature bundle is loaded on demand when the route is accessed
 */
export const router = createBrowserRouter(
  [
    // Public routes
    {
      element: <PublicLayout />,
      children: [
        {
          path: '/emergency-substitution-reply',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <ReplyPage />
            </Suspense>
          ),
        },
        {
          path: '/availability-update-reply',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <LocationAvailabilityReplyPage />
            </Suspense>
          ),
        },
        {
          path: '/onboarding/step/:token',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <PublicStepPage />
            </Suspense>
          ),
        },
      ],
    },

    // Auth routes
    {
      element: <AuthLayout />,
      children: [
        {
          path: '/login',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <LoginForm />
            </Suspense>
          ),
        },
        {
          path: '/login/:slug',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <LoginForm />
            </Suspense>
          ),
        },
        {
          path: '/auth/sso/callback',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <SSOCallback />
            </Suspense>
          ),
        },
        {
          path: '/forgot-password',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <ForgotPasswordPage />
            </Suspense>
          ),
        },
        {
          path: '/reset-password/:token',
          element: (
            <Suspense fallback={<RouteLoadingFallback />}>
              <ResetPasswordPage />
            </Suspense>
          ),
        },
      ],
    },

    // Protected routes
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <MainLayout />,
          children: [
            {
              path: '/',
              element: (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <ChatPage />
                </Suspense>
              ),
            },
            // Knowledge base routes - require MANAGE_KNOWLEDGE permission
            {
              element: <PermissionRoute permission={PERMISSIONS.MANAGE_KNOWLEDGE} redirectTo="/403" />,
              children: [
                {
                  path: '/knowledge-bases',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <KnowledgeBasesListPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/knowledge-bases/:id',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <KnowledgeBaseDetailPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/files/audit-logs',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <AuditLogsPage />
                    </Suspense>
                  ),
                },
              ],
            },
            // User management routes - require ADMIN_ACCESS permission
            {
              element: <PermissionRoute permission={PERMISSIONS.ADMIN_ACCESS} redirectTo="/403" />,
              children: [
                {
                  path: '/admin/users',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <UsersPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/admin/users/:userId',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <UserDetailPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              path: '/403',
              element: <ForbiddenPage />,
            },
            // Superadmin routes - require CROSS_ACCOUNT_ACCESS permission
            {
              element: <PermissionRoute permission={PERMISSIONS.CROSS_ACCOUNT_ACCESS} redirectTo="/403" />,
              children: [
                {
                  path: '/superadmin/conversations',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <ConversationsPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/superadmin/users',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <UsersPage />
                    </Suspense>
                  ),
                },
              ],
            },
            // Emergency substitution routes - require MANAGE_EMERGENCY permission
            {
              element: <PermissionRoute permission={PERMISSIONS.MANAGE_EMERGENCY} redirectTo="/403" />,
              children: [
                {
                  path: '/emergency-substitution',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <EmergencySubstitutionPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/create-emergency-substitution',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <CreatePage />
                    </Suspense>
                  ),
                },
                {
                  path: '/emergency-substitutions-list',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <HistoryPage />
                    </Suspense>
                  ),
                },
              ],
            },
            // Onboarding routes - require MANAGE_ONBOARDING permission
            {
              element: <PermissionRoute permission={PERMISSIONS.MANAGE_ONBOARDING} redirectTo="/403" />,
              children: [
                {
                  path: '/onboarding/workflows',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <WorkflowsListPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/onboarding/workflows/:workflowId',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <WorkflowBuilderPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/onboarding/instances',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <InstancesListPage />
                    </Suspense>
                  ),
                },
                {
                  path: '/onboarding/instances/:instanceId',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <InstanceDetailPage />
                    </Suspense>
                  ),
                },
              ],
            },
            // Engagement pulse routes - require MANAGE_ENGAGEMENT permission
            {
              element: <PermissionRoute permission={PERMISSIONS.MANAGE_ENGAGEMENT} redirectTo="/403" />,
              children: [
                {
                  path: '/engagement-pulse',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <EngagementPulsePage />
                    </Suspense>
                  ),
                },
                {
                  path: '/engagement-pulse/config',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <EngagementPulseConfigPage />
                    </Suspense>
                  ),
                },
              ],
            },
            // Admin conversation routes - require MANAGE_CONVERSATIONS permission
            {
              element: <PermissionRoute permission={PERMISSIONS.MANAGE_CONVERSATIONS} redirectTo="/403" />,
              children: [
                {
                  path: '/admin/conversations',
                  element: (
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <ConversationsPage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },

    // 404 - Catch all unmatched routes
    {
      path: '*',
      element: <PublicLayout />,
      children: [
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  }
);
